/**
 * ContactFieldRow — one row of the contact-verification card (US4).
 *
 * Mirrors the proto's `UpdateContactsModal` row: a leading field icon, a small
 * muted label, and either the read-only value or an inline `Input` (the proto
 * edits IN PLACE — the same card row flips between display and edit). The email
 * row additionally renders the domain-autocomplete dropdown beneath its input.
 *
 * Presentational + module-scope (never declared inside a render) so it stays
 * locally understandable and the parent card stays lean. All edit state lives in
 * `useContactEditForm`; this component only renders what it is handed and routes
 * change/select/dismiss callbacks back up.
 *
 * Token-backed styling only (no raw hex, no arbitrary-bracket classes): the card
 * surface is supplied by the parent (`bg-card`); the icon and label use
 * `text-muted-foreground`, the value uses `text-foreground`, the suggestion
 * popover uses the overlay-surface token (`bg-popover` / `border-border`).
 *
 * Owned by feature 026-proto-convergence-v2.
 * Tasks: T040, T041
 */

import type { ComponentType, FocusEvent, JSX, KeyboardEvent } from 'react';
import { useRef } from 'react';
import type { MessageKeys } from '../i18n/messages/index.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { Input } from '../primitives/Input.js';
import type { IconProps } from '../shell/icons.js';
import type { FieldName } from './useContactEditForm.js';

export interface ContactFieldRowProps {
  readonly field: FieldName;
  readonly icon: ComponentType<IconProps>;
  readonly label: string;
  /** HTML input type for edit mode. */
  readonly inputType: string;
  /** Autocomplete token for edit mode. */
  readonly autoComplete: string;
  /** Whether the card is in inline-edit mode (value → Input). */
  readonly editing: boolean;
  /** Current value (read display, or controlled input value in edit mode). */
  readonly value: string;
  /** Per-field validation error i18n key, or undefined when valid. */
  readonly errorKey: MessageKeys | undefined;
  /** Stable id prefix from the edit-form hook (used for label/error wiring). */
  readonly fieldIdPrefix: string;
  readonly onChange: (field: FieldName, value: string) => void;
  /**
   * Email-only: domain suggestions to render beneath the input. Empty for every
   * other field and when there is nothing to suggest.
   */
  readonly emailSuggestions?: readonly string[] | undefined;
  /** Email-only: fill the field from a chosen suggestion. */
  readonly onSelectSuggestion?: ((suggestion: string) => void) | undefined;
  /** Email-only: dismiss the suggestion list (blur / Escape). */
  readonly onDismissSuggestions?: (() => void) | undefined;
}

export function ContactFieldRow({
  field,
  icon: Icon,
  label,
  inputType,
  autoComplete,
  editing,
  value,
  errorKey,
  fieldIdPrefix,
  onChange,
  emailSuggestions = [],
  onSelectSuggestion,
  onDismissSuggestions,
}: ContactFieldRowProps): JSX.Element {
  const t = useTranslate();
  const inputId = `${fieldIdPrefix}-${field}`;
  const errorId = `${inputId}-error`;
  const showSuggestions = field === 'email' && emailSuggestions.length > 0;
  const hasSuggestionControl = field === 'email' && onDismissSuggestions !== undefined;
  // Boundary for the field group (input + suggestion buttons); used to detect when
  // focus has left the whole group so the suggestion popover can auto-dismiss.
  const groupRef = useRef<HTMLDivElement>(null);

  // Dismiss the suggestions only when focus leaves the WHOLE field group, so a Tab
  // from the input can land on a suggestion button without the list closing. Wired on
  // the semantic input/button elements (relatedTarget is the element gaining focus);
  // the wrapper is only the boundary ref, never itself interactive.
  const handleFieldBlur = hasSuggestionControl
    ? (event: FocusEvent<HTMLElement>) => {
        if (!groupRef.current?.contains(event.relatedTarget)) {
          onDismissSuggestions?.();
        }
      }
    : undefined;

  // Escape dismisses the suggestions from the input or a focused suggestion button
  // and returns focus to the input; stopPropagation keeps the takeover dialog open.
  // Wired on the semantic input/button elements (not the static wrapper).
  const handleSuggestionEscape = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape' && showSuggestions) {
      event.stopPropagation();
      onDismissSuggestions?.();
      document.getElementById(inputId)?.focus();
    }
  };

  return (
    <div className="px-6 py-4">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div
          // The field-group boundary (input + suggestion buttons) and the relative
          // positioning context for the autocomplete popover. Focus-management lives
          // on the input/buttons themselves (WCAG 2.1.1); this wrapper carries no
          // interaction, only the containment ref.
          className="relative min-w-0 flex-1"
          ref={groupRef}
        >
          {editing ? (
            // The label is a sibling of the Input (associated by htmlFor/id), not
            // a wrapper — so the error span below stays a DESCRIPTION
            // (aria-describedby) and never bleeds into the accessible NAME. That
            // keeps `getByLabelText(label)` exact even while an error shows.
            <div className="flex flex-col gap-1">
              <label htmlFor={inputId} className="text-sm font-medium text-muted-foreground">
                {label}
              </label>
              <Input
                id={inputId}
                type={inputType}
                value={value}
                autoComplete={autoComplete}
                maxLength={field === 'postalCode' ? 5 : undefined}
                aria-invalid={errorKey !== undefined}
                aria-describedby={errorKey !== undefined ? errorId : undefined}
                onChange={(event) => onChange(field, event.target.value)}
                onBlur={handleFieldBlur}
                onKeyDown={hasSuggestionControl ? handleSuggestionEscape : undefined}
              />
              {errorKey !== undefined ? (
                <span id={errorId} className="text-sm font-normal text-destructive">
                  {t(errorKey)}
                </span>
              ) : null}
            </div>
          ) : (
            <>
              <span className="block text-sm font-medium text-muted-foreground">{label}</span>
              <span className="mt-1 block truncate text-base text-foreground">{value}</span>
            </>
          )}

          {showSuggestions ? (
            // A labelled group of plain, keyboard-operable suggestion buttons (proto
            // autocomplete). Not a listbox/option pattern: there is no active-descendant
            // or arrow-key model, so those roles would be dishonest ARIA. The buttons sit
            // in the tab order and activate on click or keyboard; the group stays open
            // until a suggestion is chosen, focus leaves the field group, or Escape.
            // biome-ignore lint/a11y/useSemanticElements: role="group" gives the suggestion buttons one honest accessible name; a <fieldset> (form-control semantics, default border/padding/margin, <legend> model) does not suit an absolutely-positioned popover.
            <div
              role="group"
              aria-label={t('profile.verify.emailSuggestions.label')}
              className="absolute left-0 right-0 z-10 mt-1 overflow-hidden rounded-md border border-border bg-popover shadow-lg"
            >
              {emailSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="block w-full px-4 py-2 text-left text-base text-popover-foreground hover:bg-muted"
                  // onClick (not onMouseDown) so keyboard Enter/Space activates it;
                  // Escape (onKeyDown) dismisses. Returns focus to the email input
                  // after selecting (the list then unmounts), keeping focus order sane.
                  onClick={() => {
                    onSelectSuggestion?.(suggestion);
                    document.getElementById(inputId)?.focus();
                  }}
                  onBlur={handleFieldBlur}
                  onKeyDown={handleSuggestionEscape}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
